import { useState } from 'react';
import { View, ScrollView, Image, TouchableOpacity, Text as RNText, Dimensions } from 'react-native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ArrowLeft } from 'lucide-react-native';
import { useTrailDetail } from '@/lib/queries/trails';
import { useAuth } from '@/context/AuthContext';
import LottieLoader from '@/components/LottieLoader';
import colors from '@/constants/colors';
import theme from '@/constants/colors';

const { width: SCREEN_W, height: SCREEN_H } = Dimensions.get('window');

/** Fullscreen photo gallery for a trail (expand button on the detail hero). */
export default function TrailPhotosScreen() {
  const { id, index } = useLocalSearchParams();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { session } = useAuth();
  const trailId = typeof id === 'string' ? id : undefined;
  const trailQuery = useTrailDetail(trailId, session?.accessToken);
  const trail = trailQuery.data;

  const heroImages = trail
    ? (Array.isArray((trail as any).images)
        ? (trail as any).images.map((img: any) => (typeof img === 'string' ? img : img?.url)).filter((u: any) => typeof u === 'string' && u.length > 0)
        : [])
    : [];
  if (trail?.photo && !heroImages.includes(trail.photo)) heroImages.push(trail.photo);

  const startIndex = typeof index === 'string' && Number.isFinite(Number(index))
    ? Math.min(Math.max(0, Number(index)), Math.max(0, heroImages.length - 1))
    : 0;
  const [active, setActive] = useState(startIndex);

  if (trailQuery.isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.backgroundPrimary }}>
        <LottieLoader size={120} />
      </View>
    );
  }

  if (heroImages.length === 0) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.backgroundPrimary }}>
        <Stack.Screen options={{ headerShown: false }} />
        <TouchableOpacity
          onPress={() => router.back()}
          style={{ position: 'absolute', top: Math.max(insets.top + 12, 24), left: 16, zIndex: 10 }}
        >
          <ArrowLeft size={26} color={colors.accentPrimary} />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={{ flex: 1, backgroundColor: colors.backgroundPrimary }}>
      <Stack.Screen options={{ headerShown: false }} />

      <ScrollView
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onMomentumScrollEnd={(e) => {
          const idx = Math.round(e.nativeEvent.contentOffset.x / SCREEN_W);
          setActive(idx);
        }}
      >
        {heroImages.map((imgUrl: string, idx: number) => (
          <Image
            key={`${imgUrl}-${idx}`}
            source={{ uri: imgUrl }}
            style={{ width: SCREEN_W, height: SCREEN_H }}
            resizeMode="contain"
          />
        ))}
      </ScrollView>

      <TouchableOpacity
        onPress={() => router.back()}
        style={{
          position: 'absolute',
          top: Math.max(insets.top + 12, 24),
          left: 16,
          width: 44,
          height: 44,
          borderRadius: 22,
          backgroundColor: theme.backgroundPrimary,
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
        }}
      >
        <ArrowLeft size={24} color={colors.accentPrimary} />
      </TouchableOpacity>

      {/* Counter, same style as the detail hero's slide counter */}
      <View
        style={{
          position: 'absolute',
          top: Math.max(insets.top + 20, 32),
          alignSelf: 'center',
          paddingHorizontal: 10,
          paddingVertical: 4,
          borderRadius: 999,
          backgroundColor: theme.backgroundPrimary,
        }}
      >
        <RNText style={{ color: colors.textPrimary, fontSize: 12, fontWeight: '600' }}>
          {`${Math.min(active + 1, heroImages.length)}/${heroImages.length}`}
        </RNText>
      </View>
    </View>
  );
}